import React, { useEffect, useRef } from 'react';
import { ArrowRight, FilePlus2, GitCompare, PenLine } from 'lucide-react';
import { useResearchDemo } from '../../contexts/ResearchDemoContext';
import type { ResearchSource } from '../../types/researchDemo';
import { glassPanel, linkButton, secondaryButton } from '../../utils/demoUi';
import { claimSummary } from '../../utils/researchDemo';
import { StatusPill } from './StatusPill';
export function SourceDetail({
  source
}: {
  source: ResearchSource;
}) {
  const {
    state,
    dispatch,
    goTo,
    openClaim
  } = useResearchDemo();
  const highlightRef = useRef<HTMLQuoteElement>(null);
  const linked = state.claims.filter(claim => claim.sourceId === source.id);
  const inComparison = state.ui.compareIds.includes(source.id);
  const compareFull = !inComparison && state.ui.compareIds.length >= 4;
  const inResearch = state.ui.workspace === 'research';
  useEffect(() => {
    highlightRef.current?.scrollIntoView({
      block: 'nearest'
    });
  }, [source.id, state.ui.highlightPassageId]);
  const writeNote = () => {
    dispatch({
      type: 'setNoteDraft',
      draft: {
        sourceId: source.id
      }
    });
    goTo('research', 'notebook');
  };
  const propose = (passageId: string) => {
    dispatch({
      type: 'setProposalDraft',
      draft: {
        sourceId: source.id,
        passageId
      }
    });
    goTo('curation', 'propose');
  };
  const facts: [string, string][] = [['Discipline', source.discipline], ['Study design', source.design], ['Population', source.population], ['Method', source.method], ['Finding', source.finding], ['Interpretation limits', source.limit]];
  return <article aria-labelledby="source-detail-title" className={`${glassPanel} p-6 lg:p-8`} data-id="element-2333" data-mp="3or36pih8nuly">
      <p className="font-mono text-xs font-semibold text-ink-muted" data-id="element-2334" data-mp="3oopcfbudumfg">{source.id}</p>
      <h2 id="source-detail-title" className="mt-1 font-heading text-2xl font-bold leading-snug text-ink-dark" data-id="element-2335" data-mp="3jpvzplmx6zgc">
        {source.title}
      </h2>
      <p className="mt-1 text-sm font-medium text-lavender" data-id="element-2336" data-mp="1pehd3dp7hg4t">{source.discipline}</p>

      <div className="mt-5 flex flex-wrap gap-2" data-id="element-2337" data-mp="3pggud5zpoxed">
        <button type="button" onClick={() => dispatch({
        type: 'toggleCompare',
        id: source.id
      })} disabled={compareFull} aria-pressed={inComparison} className={secondaryButton} data-id="element-2338" data-mp="3phrwz2hf7z3w">
          <GitCompare size={16} aria-hidden="true" data-id="element-2339" data-mp="7am12vih9pxp" />
          {inComparison ? 'In comparison' : 'Add to comparison'}
        </button>
        <button type="button" onClick={writeNote} className={secondaryButton} data-id="element-2340" data-mp="cagmwknebpbw">
          <PenLine size={16} aria-hidden="true" data-id="element-2341" data-mp="h88h5lbijnj9" />
          Write a linked note
          {!inResearch && <span className="sr-only" data-id="element-2342" data-mp="lzqkgg10e07e">(opens the Research Workspace)</span>}
        </button>
        <button type="button" onClick={() => propose(source.passages[0].id)} className={secondaryButton} data-id="element-2343" data-mp="qyl8bwdu2eyd">
          <FilePlus2 size={16} aria-hidden="true" data-id="element-2344" data-mp="v0gbz7j8l4fb" />
          Propose a relationship
          {inResearch && <span className="sr-only" data-id="element-2345" data-mp="zyzwr4m3ga63">(opens Curation &amp; Administration)</span>}
        </button>
      </div>
      {compareFull && <p className="mt-2 text-xs text-ink-muted" data-id="element-2346" data-mp="2kid612x37ymn">Four studies are already selected for comparison.</p>}

      <section aria-labelledby="source-facts" className="mt-8" data-id="element-2347" data-mp="2fh5wozfaqrdv">
        <h3 id="source-facts" className="sr-only" data-id="element-2348" data-mp="2bgcacahi5kn3">
          Study details
        </h3>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2" data-id="element-2349" data-mp="26hiwggyazrme">
          {facts.map(([term, value]) => <div key={term} data-id="element-2350" data-mp="338bdbgt29np8">
              <dt className="text-sm font-medium text-ink-muted" data-id="element-2351" data-mp="336i5vzamizg6">{term}</dt>
              <dd className="mt-0.5 text-sm leading-relaxed text-ink-dark" data-id="element-2352" data-mp="2z5okpdokfyqz">{value}</dd>
            </div>)}
        </dl>
      </section>

      <section aria-labelledby="source-passages" className="mt-8" data-id="element-2353" data-mp="2udnaryevek44">
        <h3 id="source-passages" className="font-heading text-sm font-bold text-ink-dark" data-id="element-2354" data-mp="2pfc0jmkny7ld">
          Source passages
        </h3>
        <p className="mt-1 text-xs text-ink-muted" data-id="element-2355" data-mp="3mx3pdd3tes5e">Exact source text. Reviewer interpretations are recorded separately.</p>
        <ul className="mt-3 space-y-3" data-id="element-2356" data-mp="3i37wgp2ervh3">
          {source.passages.map(passage => {
          const highlighted = state.ui.highlightPassageId === passage.id;
          return <li key={passage.id} data-id="element-2357" data-mp="36mjoyxbyroof">
                <blockquote ref={highlighted ? highlightRef : undefined} className={`rounded-xl border-l-4 px-5 py-4 transition-colors duration-200 ${highlighted ? 'border-lavender bg-lavender-pale' : 'border-navy/30 bg-surface-bg'}`} data-id="element-2358" data-mp="2spekh1rwexva">
                  <p className="font-mono text-xs font-semibold text-ink-muted" data-id="element-2359" data-mp="3pdueba21jte7">
                    Source text · {source.id} · {passage.id}
                  </p>
                  <p className="mt-2 font-serif text-[1.05rem] leading-relaxed text-ink-dark" data-id="element-2360" data-mp="3gmajv04fs5p1">“{passage.text}”</p>
                  <div className="mt-3 flex flex-wrap gap-4" data-id="element-2361" data-mp="86qsk6nbsxcm">
                    <button type="button" onClick={() => dispatch({
                  type: 'selectSource',
                  id: source.id,
                  passageId: passage.id
                })} aria-pressed={highlighted} className={linkButton} data-id="element-2362" data-mp="8562at81o0kk">
                      {highlighted ? 'Passage selected' : 'Select passage'}
                    </button>
                    <button type="button" onClick={() => propose(passage.id)} className={linkButton} data-id="element-2363" data-mp="w46mqyouq2me">
                      Propose from {passage.id}
                      <ArrowRight size={14} aria-hidden="true" data-id="element-2364" data-mp="3eq1qm3k9o07" />
                    </button>
                  </div>
                </blockquote>
              </li>;
        })}
        </ul>
      </section>

      <section aria-labelledby="linked-records" className="mt-8" data-id="element-2365" data-mp="ddfiucnmv4fe">
        <h3 id="linked-records" className="font-heading text-sm font-bold text-ink-dark" data-id="element-2366" data-mp="8ci1g2ag6eey">
          Linked relationship records
        </h3>
        {linked.length === 0 ? <p className="mt-2 text-sm text-ink-muted" data-id="element-2367" data-mp="1jrwuiuzjcm4s">No relationships cite this source yet.</p> : <ul className="mt-3 divide-y divide-line border-y border-line" data-id="element-2368" data-mp="1rsw4os4wfl6d">
            {linked.map(claim => <li key={claim.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-2369" data-mp="1vtg5c5ool6x9">
                <div className="min-w-0" data-id="element-2370" data-mp="20ulyu5q60y7u">
                  <p className="text-sm font-semibold text-ink-dark" data-id="element-2371" data-mp="20rhoeqm3x68q">{claimSummary(claim)}</p>
                  <p className="text-xs text-ink-muted" data-id="element-2372" data-mp="25q1aqs4m4k1m">
                    {claim.id} · {claim.passageId} · v{claim.version}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-3" data-id="element-2373" data-mp="1d49ge0xanj30">
                  <StatusPill status={claim.status} data-id="element-2374" data-mp="1i2jcqeur8pq7" />
                  <button type="button" onClick={() => openClaim(claim.id)} className={linkButton} data-id="element-2375" data-mp="1i0q62zk565i7">
                    Inspect evidence record
                    <span className="sr-only" data-id="element-2376" data-mp="pexbhd901x6i"> {claim.id}</span>
                  </button>
                </div>
              </li>)}
          </ul>}
      </section>
    </article>;
}